/**
 * Computes the position range [minX, maxX, minY, maxY] that keeps a
 * (potentially rotated) element's visual footprint within template bounds.
 *
 * Rotation is around the element center (CSS transformOrigin: 50% 50%).
 * The axis-aligned bounding box (AABB) of the rotated rectangle is used.
 */
export const getRotatedBoundsClamp = (
  width: number,
  height: number,
  rotation: number, // degrees
  templateWidth: number,
  templateHeight: number,
): { minX: number; maxX: number; minY: number; maxY: number } => {
  const rad = (rotation * Math.PI) / 180
  const cos = Math.abs(Math.cos(rad))
  const sin = Math.abs(Math.sin(rad))

  // Half-extents of the axis-aligned bounding box
  const halfAABBw = (width * cos + height * sin) / 2
  const halfAABBh = (width * sin + height * cos) / 2

  // The element center (cx, cy) = (x + w/2, y + h/2).
  // For the AABB to stay inside canvas: cx ∈ [halfAABBw, templateWidth - halfAABBw]
  // Converting back to x: x ∈ [halfAABBw - w/2, templateWidth - halfAABBw - w/2]
  return {
    minX: halfAABBw - width / 2,
    maxX: templateWidth - halfAABBw - width / 2,
    minY: halfAABBh - height / 2,
    maxY: templateHeight - halfAABBh - height / 2,
  }
}

/**
 * Restricts movement delta to keep elements within template bounds.
 *
 * When moving multiple elements, uses the most restrictive boundary
 * across all elements to ensure none go outside the template.
 *
 * @param deltaX - Desired horizontal movement
 * @param deltaY - Desired vertical movement
 * @param elements - Elements being moved (with x, y, width, height, optional rotation)
 * @param templateWidth - Template width in mm
 * @param templateHeight - Template height in mm
 * @returns Restricted movement delta that keeps all elements within bounds
 */
const restrictMovementToBounds = (
  deltaX: number,
  deltaY: number,
  elements: Array<{ x: number; y: number; width: number; height: number; rotation?: number }>,
  templateWidth: number,
  templateHeight: number,
): { deltaX: number; deltaY: number } => {
  let clampedDeltaX = deltaX
  let clampedDeltaY = deltaY

  elements.forEach((element) => {
    const bounds = getRotatedBoundsClamp(
      element.width, element.height, element.rotation || 0, templateWidth, templateHeight,
    )

    // Clamp X: check left and right boundaries
    if (deltaX < 0) {
      const maxLeftMove = bounds.minX - element.x
      clampedDeltaX = Math.max(clampedDeltaX, maxLeftMove)
    } else if (deltaX > 0) {
      const maxRightMove = bounds.maxX - element.x
      clampedDeltaX = Math.min(clampedDeltaX, maxRightMove)
    }

    // Clamp Y: check top and bottom boundaries
    if (deltaY < 0) {
      const maxUpMove = bounds.minY - element.y
      clampedDeltaY = Math.max(clampedDeltaY, maxUpMove)
    } else if (deltaY > 0) {
      const maxDownMove = bounds.maxY - element.y
      clampedDeltaY = Math.min(clampedDeltaY, maxDownMove)
    }
  })

  return { deltaX: clampedDeltaX, deltaY: clampedDeltaY }
}

export default restrictMovementToBounds
